import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GraphQLClient } from "graphql-request";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import FitScreenOutlinedIcon from "@mui/icons-material/FitScreenOutlined";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton.jsx";
import { useTenant } from "../../context/TenantContext.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config.js";
import {
  READ_BUSINESS_STRUCTURES,
  READ_PLAYBOOKS_FOR_STRUCTURE,
} from "../../graph/businessStructure/queries.js";
import {
  CREATE_BUSINESS_STRUCTURE,
  ADD_BUSINESS_STRUCTURE_NODE,
  UPDATE_BUSINESS_STRUCTURE_NODE,
  REMOVE_BUSINESS_STRUCTURE_NODE,
  ADD_BUSINESS_STRUCTURE_LINK,
  UPDATE_BUSINESS_STRUCTURE_LINK,
  REMOVE_BUSINESS_STRUCTURE_LINK,
} from "../../graph/businessStructure/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);
const NODE_WIDTH = 270;
const NODE_HEIGHT = 148;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.1;

const getNodeTitle = (node) =>
  node.kind === "PLAYBOOK"
    ? node.playbook?.name || "Playbook"
    : node.label || "Untitled gap";

const getInitialPosition = (index) => ({
  positionX: 48 + (index % 3) * 330,
  positionY: 48 + Math.floor(index / 3) * 210,
});

const wrapLinkLabel = (value, maxCharacters = 30) => {
  const words = value.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxCharacters && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const ManageBusinessStructure = () => {
  const navigate = useNavigate();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const requestSequence = useRef(0);
  const nodeRefs = useRef(new Map());
  const [nodeSizes, setNodeSizes] = useState({});

  const [structures, setStructures] = useState([]);
  const [selectedStructureId, setSelectedStructureId] = useState("");
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [loadError, setLoadError] = useState("");

  const [createStructureOpen, setCreateStructureOpen] = useState(false);
  const [structureName, setStructureName] = useState("Business operating model");
  const [structureDescription, setStructureDescription] = useState("");

  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [nodeKind, setNodeKind] = useState("PLAYBOOK");
  const [nodePlaybookId, setNodePlaybookId] = useState("");
  const [nodeLabel, setNodeLabel] = useState("");
  const [nodeNotes, setNodeNotes] = useState("");
  const [nodeStage, setNodeStage] = useState("");

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkFromNodeId, setLinkFromNodeId] = useState("");
  const [linkToNodeId, setLinkToNodeId] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkOutcomeKey, setLinkOutcomeKey] = useState("");
  const [linkIsDefault, setLinkIsDefault] = useState(false);

  const selectedStructure = useMemo(
    () => structures.find((structure) => structure.id === selectedStructureId) || null,
    [structures, selectedStructureId]
  );
  const nodes = selectedStructure?.nodes || [];
  const links = selectedStructure?.links || [];

  const notify = useCallback(
    (severity, message) =>
      setSnackbar({ open: true, severity, message }),
    [setSnackbar]
  );

  const loadTenantData = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!tenantId) {
      setStructures([]);
      setSelectedStructureId("");
      setPlaybooks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");
    // Clear the previous tenant's data immediately during a tenant switch.
    setStructures([]);
    setSelectedStructureId("");
    setPlaybooks([]);

    try {
      const [structureResult, playbookResult] = await Promise.all([
        client.request(READ_BUSINESS_STRUCTURES, { tenantId }),
        client.request(READ_PLAYBOOKS_FOR_STRUCTURE, { tenantId }),
      ]);
      if (requestId !== requestSequence.current) return;

      const tenantStructures = (structureResult.readBusinessStructures || []).filter(
        (structure) => structure.tenantId === tenantId
      );
      const tenantPlaybooks = (playbookResult.readPlaybooks || []).filter(
        (playbook) => playbook.tenantId === tenantId
      );

      setStructures(tenantStructures);
      setSelectedStructureId(tenantStructures[0]?.id || "");
      setPlaybooks(tenantPlaybooks);
    } catch (error) {
      if (requestId !== requestSequence.current) return;
      console.error("Failed to load business structure:", error);
      setLoadError("Business structure could not be loaded.");
      notify("error", "Failed to load business structure");
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [tenantId, notify]);

  useEffect(() => {
    loadTenantData();
    return () => {
      requestSequence.current += 1;
    };
  }, [loadTenantData]);

  useEffect(() => {
    setZoom(1);
  }, [selectedStructureId]);

  useEffect(() => {
  const measuredSizes = {};

  nodes.forEach((node) => {
    const element = nodeRefs.current.get(node.id);
    if (element) {
      measuredSizes[node.id] = {
        width: element.offsetWidth,
        height: element.offsetHeight,
      };
    }
  });

  setNodeSizes((current) => {
    const sameKeys =
      Object.keys(current).length === Object.keys(measuredSizes).length;

    const sameSizes = Object.entries(measuredSizes).every(([id, size]) =>
      current[id]?.width === size.width &&
      current[id]?.height === size.height
    );

    return sameKeys && sameSizes ? current : measuredSizes;
  });
}, [nodes]);

  const updateStructure = useCallback((structureId, updater) => {
    setStructures((current) =>
      current.map((structure) =>
        structure.id === structureId ? updater(structure) : structure
      )
    );
  }, []);

  const handleCreateStructure = async () => {
    if (!tenantId || !structureName.trim()) return;
    setSaving(true);
    try {
      const result = await client.request(CREATE_BUSINESS_STRUCTURE, {
        input: {
          tenantId,
          name: structureName.trim(),
          description: structureDescription.trim() || null,
        },
      });
      const created = {
        ...result.createBusinessStructure,
        nodes: [],
        links: [],
      };
      setStructures((current) => [created, ...current]);
      setSelectedStructureId(created.id);
      setCreateStructureOpen(false);
      setStructureName("Business operating model");
      setStructureDescription("");
      notify("success", "Business structure created");
    } catch (error) {
      console.error("Failed to create business structure:", error);
      notify("error", "Could not create business structure");
    } finally {
      setSaving(false);
    }
  };

  const openAddNode = (kind) => {
    setEditingNode(null);
    setNodeKind(kind);
    setNodePlaybookId(playbooks[0]?.id || "");
    setNodeLabel("");
    setNodeNotes("");
    setNodeStage("");
    setNodeDialogOpen(true);
  };

  const openEditNode = (node) => {
    setEditingNode(node);
    setNodeKind(node.kind);
    setNodePlaybookId(node.playbookId || "");
    setNodeLabel(node.label || "");
    setNodeNotes(node.notes || "");
    setNodeStage(node.stage || "");
    setNodeDialogOpen(true);
  };

  const handleSaveNode = async () => {
    if (!selectedStructure) return;
    if (nodeKind === "PLAYBOOK" && !nodePlaybookId) {
      notify("warning", "Choose a playbook to add");
      return;
    }
    if (nodeKind === "GAP" && !nodeLabel.trim()) {
      notify("warning", "Give the gap a name");
      return;
    }

    setSaving(true);
    try {
      if (editingNode) {
        const result = await client.request(UPDATE_BUSINESS_STRUCTURE_NODE, {
          input: {
            id: editingNode.id,
            kind: nodeKind,
            playbookId: nodeKind === "PLAYBOOK" ? nodePlaybookId : null,
            label: nodeKind === "GAP" ? nodeLabel.trim() : null,
            notes: nodeNotes.trim() || null,
            stage: nodeStage.trim() || null,
          },
        });
        const updated = result.updateBusinessStructureNode;
        updateStructure(selectedStructure.id, (structure) => ({
          ...structure,
          nodes: structure.nodes.map((node) =>
            node.id === updated.id ? updated : node
          ),
        }));
      } else {
        const position = getInitialPosition(nodes.length);
        const result = await client.request(ADD_BUSINESS_STRUCTURE_NODE, {
          input: {
            businessStructureId: selectedStructure.id,
            kind: nodeKind,
            playbookId: nodeKind === "PLAYBOOK" ? nodePlaybookId : null,
            label: nodeKind === "GAP" ? nodeLabel.trim() : null,
            notes: nodeNotes.trim() || null,
            stage: nodeStage.trim() || null,
            ...position,
          },
        });
        const created = result.addBusinessStructureNode;
        updateStructure(selectedStructure.id, (structure) => ({
          ...structure,
          nodes: [...structure.nodes, created],
        }));
      }
      setNodeDialogOpen(false);
      notify("success", editingNode ? "Map item updated" : "Map item added");
    } catch (error) {
      console.error("Failed to save business structure node:", error);
      notify("error", "Could not save map item");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveNode = async (node) => {
    if (!window.confirm(`Remove “${getNodeTitle(node)}” from this map?`)) return;
    try {
      await client.request(REMOVE_BUSINESS_STRUCTURE_NODE, {
        input: { id: node.id },
      });
      updateStructure(selectedStructure.id, (structure) => ({
        ...structure,
        nodes: structure.nodes.filter((item) => item.id !== node.id),
        links: structure.links.filter(
          (link) => link.fromNodeId !== node.id && link.toNodeId !== node.id
        ),
      }));
      notify("success", "Map item removed");
    } catch (error) {
      console.error("Failed to remove business structure node:", error);
      notify("error", "Could not remove map item");
    }
  };

  const openAddLink = () => {
    setEditingLink(null);
    setLinkFromNodeId(nodes[0]?.id || "");
    setLinkToNodeId(nodes[1]?.id || "");
    setLinkLabel("");
    setLinkOutcomeKey("");
    setLinkIsDefault(false);
    setLinkDialogOpen(true);
  };

  const openEditLink = (link) => {
    setEditingLink(link);
    setLinkFromNodeId(link.fromNodeId);
    setLinkToNodeId(link.toNodeId);
    setLinkLabel(link.label || "");
    setLinkOutcomeKey(link.outcomeKey || "");
    setLinkIsDefault(Boolean(link.isDefault));
    setLinkDialogOpen(true);
  };

  const handleSaveLink = async () => {
    if (!selectedStructure || !linkFromNodeId || !linkToNodeId) return;
    if (linkFromNodeId === linkToNodeId) {
      notify("warning", "A map item cannot connect to itself");
      return;
    }
    setSaving(true);
    try {
      if (editingLink) {
        const result = await client.request(UPDATE_BUSINESS_STRUCTURE_LINK, {
          input: {
            id: editingLink.id,
            label: linkLabel.trim() || null,
            outcomeKey: linkOutcomeKey.trim() || null,
            isDefault: linkIsDefault,
          },
        });
        const updated = result.updateBusinessStructureLink;
        updateStructure(selectedStructure.id, (structure) => ({
          ...structure,
          links: structure.links.map((link) =>
            link.id === updated.id ? updated : link
          ),
        }));
      } else {
        const result = await client.request(ADD_BUSINESS_STRUCTURE_LINK, {
          input: {
            businessStructureId: selectedStructure.id,
            fromNodeId: linkFromNodeId,
            toNodeId: linkToNodeId,
            label: linkLabel.trim() || null,
            outcomeKey: linkOutcomeKey.trim() || null,
            isDefault: linkIsDefault,
            sortOrder: links.length,
          },
        });
        const created = result.addBusinessStructureLink;
        updateStructure(selectedStructure.id, (structure) => ({
          ...structure,
          links: [...structure.links, created],
        }));
      }
      setLinkDialogOpen(false);
      notify("success", editingLink ? "Connection updated" : "Connection added");
    } catch (error) {
      console.error("Failed to save business structure link:", error);
      notify("error", "Could not save connection");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLink = async (link) => {
    if (!window.confirm("Remove this connection from the map?")) return;
    try {
      await client.request(REMOVE_BUSINESS_STRUCTURE_LINK, {
        input: { id: link.id },
      });
      updateStructure(selectedStructure.id, (structure) => ({
        ...structure,
        links: structure.links.filter((item) => item.id !== link.id),
      }));
      notify("success", "Connection removed");
    } catch (error) {
      console.error("Failed to remove business structure link:", error);
      notify("error", "Could not remove connection");
    }
  };

  const startDrag = (event, node) => {
    if (event.target.closest("button")) return;
    event.preventDefault();
    const nodeX = node.positionX ?? 48;
    const nodeY = node.positionY ?? 48;
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    const pointerWorldX =
      ((event.clientX - (canvasRect?.left || 0)) + (canvasRef.current?.scrollLeft || 0)) / zoom;
    const pointerWorldY =
      ((event.clientY - (canvasRect?.top || 0)) + (canvasRef.current?.scrollTop || 0)) / zoom;
    dragRef.current = {
      nodeId: node.id,
      pointerId: event.pointerId,
      offsetX: pointerWorldX - nodeX,
      offsetY: pointerWorldY - nodeY,
      lastX: nodeX,
      lastY: nodeY,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(
      16,
      (event.clientX - rect.left + canvasRef.current.scrollLeft) / zoom - drag.offsetX
    );
    const y = Math.max(
      16,
      (event.clientY - rect.top + canvasRef.current.scrollTop) / zoom - drag.offsetY
    );
    drag.lastX = x;
    drag.lastY = y;
    updateStructure(selectedStructure.id, (structure) => ({
      ...structure,
      nodes: structure.nodes.map((node) =>
        node.id === drag.nodeId ? { ...node, positionX: x, positionY: y } : node
      ),
    }));
  };

  const endDrag = async () => {
    const drag = dragRef.current;
    if (!drag || !selectedStructure) return;
    dragRef.current = null;
    try {
      await client.request(UPDATE_BUSINESS_STRUCTURE_NODE, {
        input: {
          id: drag.nodeId,
          positionX: drag.lastX,
          positionY: drag.lastY,
        },
      });
    } catch (error) {
      console.error("Failed to save map item position:", error);
      notify("error", "Could not save the card position");
      loadTenantData();
    }
  };

  const canvasBounds = useMemo(() => {
    const maxX = Math.max(1100, ...nodes.map((node) => (node.positionX ?? 48) + NODE_WIDTH + 80));
    const maxY = Math.max(650, ...nodes.map((node) => (node.positionY ?? 48) + NODE_HEIGHT + 80));
    return { width: maxX, height: maxY };
  }, [nodes]);

  const fitCanvasToView = () => {
    if (!canvasRef.current) return;
    const horizontalFit = (canvasRef.current.clientWidth - 40) / canvasBounds.width;
    const verticalFit = (canvasRef.current.clientHeight - 40) / canvasBounds.height;
    const fittedZoom = Math.min(
      MAX_ZOOM,
      Math.max(MIN_ZOOM, Math.min(horizontalFit, verticalFit))
    );
    setZoom(fittedZoom);
    canvasRef.current.scrollTo({ left: 0, top: 0, behavior: "smooth" });
  };

  const nodePosition = (node) => ({
    x: node.positionX ?? 48,
    y: node.positionY ?? 48,
  });

    const renderLinkLine = (link) => {
    const from = nodes.find((node) => node.id === link.fromNodeId);
    const to = nodes.find((node) => node.id === link.toNodeId);
    if (!from || !to) return null;

  const a = nodePosition(from);
const b = nodePosition(to);
const fromSize = nodeSizes[from.id] || {
  width: NODE_WIDTH,
  height: NODE_HEIGHT,
};
const toSize = nodeSizes[to.id] || {
  width: NODE_WIDTH,
  height: NODE_HEIGHT,
};

const fromCenterX = a.x + fromSize.width / 2;
const fromCenterY = a.y + fromSize.height / 2;
const toCenterX = b.x + toSize.width / 2;
const toCenterY = b.y + toSize.height / 2;

const deltaX = toCenterX - fromCenterX;
const deltaY = toCenterY - fromCenterY;
const distance = Math.hypot(deltaX, deltaY);
if (!distance) return null;

const directionX = deltaX / distance;
const directionY = deltaY / distance;

const edgeDistance = (unitX, unitY, size) =>
  Math.min(
    unitX === 0 ? Infinity : size.width / 2 / Math.abs(unitX),
    unitY === 0 ? Infinity : size.height / 2 / Math.abs(unitY)
  );

const fromEdge = edgeDistance(directionX, directionY, fromSize) + 3;
const toEdge = edgeDistance(directionX, directionY, toSize) + 10;

const startX = fromCenterX + directionX * fromEdge;
const startY = fromCenterY + directionY * fromEdge;
const endX = toCenterX - directionX * toEdge;
const endY = toCenterY - directionY * toEdge;

const middleX = (startX + endX) / 2;
const middleY = (startY + endY) / 2;
    const labelLines = link.label ? wrapLinkLabel(link.label) : [];
    const labelWidth = labelLines.length
      ? Math.min(280, Math.max(100, ...labelLines.map((line) => line.length * 7 + 20)))
      : 0;
    const labelHeight = labelLines.length ? labelLines.length * 17 + 10 : 0;

    return (
      <g key={link.id}>
        <path
          d={`M ${startX} ${startY} L ${endX} ${endY}`}
          fill="none"
          stroke="#4aa6a1"
          strokeWidth="2.5"
          markerEnd="url(#business-structure-arrow)"
        />

        {labelLines.length > 0 && (
          <g pointerEvents="none">
            <rect
              x={middleX - labelWidth / 2}
              y={middleY - labelHeight / 2}
              width={labelWidth}
              height={labelHeight}
              rx="6"
              fill="rgba(82, 86, 90, 0.96)"
              stroke="rgba(255, 255, 255, 0.22)"
              strokeWidth="1"
            />
            <text
              x={middleX}
              y={middleY - ((labelLines.length - 1) * 17) / 2 + 4}
              textAnchor="middle"
              fill="#ffffff"
              fontSize="12"
              fontWeight="600"
            >
              {labelLines.map((line, index) => (
                <tspan
                  key={`${link.id}-label-${index}`}
                  x={middleX}
                  dy={index === 0 ? 0 : 17}
                >
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        )}
      </g>
    );
  };

  const selectedPlaybook = playbooks.find((item) => item.id === nodePlaybookId);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={3}>
        <Header
          title="BUSINESS STRUCTURE"
          subtitle={tenantName ? `Map how work flows across ${tenantName}` : "Map how work flows across this tenant"}
        />
        <ActionButton tone="gold" onClick={() => setCreateStructureOpen(true)} disabled={!tenantId}>
          Create Business Structure
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Alert severity="info">Select a tenant in the top bar to view its business structure.</Alert>
      ) : loading ? (
        <Box display="flex" alignItems="center" gap={1.5} py={5}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading this tenant’s business structure…</Typography>
        </Box>
      ) : loadError ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadTenantData}>Retry</Button>}>
          {loadError}
        </Alert>
      ) : structures.length === 0 ? (
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 6 }, textAlign: "center" }}>
          <AccountTreeOutlinedIcon color="secondary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>No business structure yet</Typography>
          <Typography color="text.secondary" maxWidth={620} mx="auto" mb={2.5}>
            Create a map for this tenant. Add playbooks where work is already defined, connect the steps, and mark gaps as you find them.
          </Typography>
          <ActionButton tone="gold" onClick={() => setCreateStructureOpen(true)}>Create Business Structure</ActionButton>
        </Paper>
      ) : (
        <>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ lg: "center" }} mb={2}>
            {structures.length > 1 && (
              <TextField
                select
                size="small"
                label="Business structure"
                value={selectedStructureId}
                onChange={(event) => setSelectedStructureId(event.target.value)}
                sx={{ minWidth: 250 }}
              >
                {structures.map((structure) => <MenuItem key={structure.id} value={structure.id}>{structure.name}</MenuItem>)}
              </TextField>
            )}
            <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1 }}>
              {selectedStructure?.name}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button startIcon={<AddOutlinedIcon />} variant="outlined" onClick={() => openAddNode("PLAYBOOK")} disabled={!playbooks.length}>
                Add playbook
              </Button>
              <Button startIcon={<AddOutlinedIcon />} variant="outlined" onClick={() => openAddNode("GAP")}>
                Add gap
              </Button>
              <Button startIcon={<LinkOutlinedIcon />} variant="contained" onClick={openAddLink} disabled={nodes.length < 2}>
                Connect items
              </Button>
            </Stack>
          </Stack>

          {selectedStructure?.description && (
            <Typography color="text.secondary" mb={2}>{selectedStructure.description}</Typography>
          )}

          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">Drag cards to arrange the flow. Select edit to change details.</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="Zoom out">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))))}
                      disabled={zoom <= MIN_ZOOM}
                    >
                      <ZoomOutOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="caption" sx={{ minWidth: 38, textAlign: "center" }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))))}
                      disabled={zoom >= MAX_ZOOM}
                    >
                      <ZoomInOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Fit map to view">
                  <IconButton size="small" onClick={fitCanvasToView}>
                    <FitScreenOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Chip size="small" label={`${nodes.length} ${nodes.length === 1 ? "item" : "items"}`} />
                <Chip size="small" label={`${links.length} ${links.length === 1 ? "connection" : "connections"}`} />
              </Stack>
            </Box>
            <Box
              ref={canvasRef}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              sx={{ height: { xs: 672, md: 816 }, overflow: "auto", position: "relative", bgcolor: "background.default" }}
            >
              {nodes.length === 0 ? (
                <Box sx={{ position: "sticky", left: 0, top: 0, minHeight: 560, display: "grid", placeItems: "center", p: 3, textAlign: "center" }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>Start building the map</Typography>
                    <Typography color="text.secondary" mb={2}>Add a playbook or mark a gap, then connect the steps.</Typography>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button variant="outlined" onClick={() => openAddNode("PLAYBOOK")} disabled={!playbooks.length}>Add playbook</Button>
                      <Button variant="outlined" onClick={() => openAddNode("GAP")}>Add gap</Button>
                    </Stack>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ width: canvasBounds.width * zoom, height: canvasBounds.height * zoom, position: "relative" }}>
                  <Box sx={{ width: canvasBounds.width, height: canvasBounds.height, position: "absolute", left: 0, top: 0, transform: `scale(${zoom})`, transformOrigin: "top left", backgroundImage: "radial-gradient(currentColor 0.7px, transparent 0.7px)", backgroundSize: "22px 22px", color: "text.disabled" }}>
                  <svg width={canvasBounds.width} height={canvasBounds.height} style={{ position: "absolute", inset: 0, overflow: "visible", pointerEvents: "none" }}>
                    <defs>
                      <marker id="business-structure-arrow" viewBox="0 0 10 10" markerWidth="12" markerHeight="12" refX="9" refY="5" orient="auto" markerUnits="userSpaceOnUse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#4aa6a1" />
                      </marker>
                    </defs>
                    {links.map(renderLinkLine)}
                  </svg>

                  {nodes.map((node) => {
                    const pos = nodePosition(node);
                    const title = getNodeTitle(node);
                    return (
                      <Paper
                      key={node.id}
                      ref={(element) => {
                        if (element) nodeRefs.current.set(node.id, element);
                        else nodeRefs.current.delete(node.id);
                      }}
                      variant="outlined"
                        sx={{
                          position: "absolute",
                          left: pos.x,
                          top: pos.y,
                          width: NODE_WIDTH,
                          minHeight: NODE_HEIGHT,
                          p: 1.5,
                          color: "text.primary",
                          borderColor: node.kind === "GAP" ? "warning.main" : "secondary.main",
                          borderWidth: 1.5,
                          boxShadow: 3,
                        }}
                      >
                        <Box
                          onPointerDown={(event) => startDrag(event, node)}
                          sx={{ cursor: "grab", touchAction: "none", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}
                        >
                          <Box minWidth={0}>
                            <Chip size="small" label={node.kind === "GAP" ? "Gap" : "Playbook"} color={node.kind === "GAP" ? "warning" : "secondary"} sx={{ mb: 0.8 }} />
                            <Typography fontWeight={700} sx={{ lineHeight: 1.25, overflowWrap: "anywhere" }}>{title}</Typography>
                          </Box>
                          <Stack direction="row" spacing={0}>
                            <Tooltip title="Edit item"><IconButton size="small" onClick={() => openEditNode(node)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                            {node.kind === "PLAYBOOK" && node.playbookId && (
                              <Tooltip title="Open playbook"><IconButton size="small" onClick={() => navigate(`/playbooks/read/${node.playbookId}`, { state: { redirectPath: "/manageBusinessStructure" } })}><OpenInNewOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                            )}
                            <Tooltip title="Remove from map"><IconButton size="small" onClick={() => handleRemoveNode(node)}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                          </Stack>
                        </Box>
                        {node.stage && <Typography variant="caption" color="secondary.main" display="block" mb={0.5}>{node.stage}</Typography>}
                        {node.kind === "PLAYBOOK" && node.playbook?.objective && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {node.playbook.objective}
                          </Typography>
                        )}
                        {node.kind === "GAP" && node.notes && <Typography variant="body2" color="text.secondary">{node.notes}</Typography>}
                      </Paper>
                    );
                  })}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>

          {links.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="h6" fontWeight={700} mb={1}>Connections</Typography>
              <Stack divider={<Divider flexItem />}>
                {links.map((link) => {
                  const from = nodes.find((node) => node.id === link.fromNodeId);
                  const to = nodes.find((node) => node.id === link.toNodeId);
                  return (
                    <Box key={link.id} display="flex" alignItems="center" gap={1} py={1} flexWrap="wrap">
                      <Typography>{from ? getNodeTitle(from) : "Missing item"}</Typography>
                      <LinkOutlinedIcon fontSize="small" color="secondary" />
                      <Typography>{to ? getNodeTitle(to) : "Missing item"}</Typography>
                      {link.label && (
                        <Chip
                          size="small"
                          label={link.label}
                          sx={{
                            bgcolor: "grey.700",
                            color: "#fff",
                            fontWeight: 700,
                            "& .MuiChip-label": { fontWeight: 700 },
                          }}
                        />
                      )}
                      {link.isDefault && <Chip size="small" color="secondary" label="Default" />}
                      <Box flexGrow={1} />
                      <Tooltip title="Edit connection"><IconButton size="small" onClick={() => openEditLink(link)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Remove connection"><IconButton size="small" onClick={() => handleRemoveLink(link)}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </>
      )}

      <Dialog open={createStructureOpen} onClose={() => setCreateStructureOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create business structure</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField autoFocus label="Name" value={structureName} onChange={(event) => setStructureName(event.target.value)} required />
            <TextField label="Description" value={structureDescription} onChange={(event) => setStructureDescription(event.target.value)} multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateStructureOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateStructure} disabled={saving || !structureName.trim()}>{saving ? "Creating…" : "Create and open"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nodeDialogOpen} onClose={() => setNodeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingNode ? "Edit map item" : nodeKind === "GAP" ? "Add a gap" : "Add a playbook"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            {!editingNode && (
              <TextField select label="Item type" value={nodeKind} onChange={(event) => setNodeKind(event.target.value)}>
                <MenuItem value="PLAYBOOK">Existing playbook</MenuItem>
                <MenuItem value="GAP">Gap to define</MenuItem>
              </TextField>
            )}
            {nodeKind === "PLAYBOOK" ? (
              <TextField select label="Playbook" value={nodePlaybookId} onChange={(event) => setNodePlaybookId(event.target.value)} required>
                {playbooks.map((playbook) => <MenuItem key={playbook.id} value={playbook.id}>{playbook.name}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="Gap name" value={nodeLabel} onChange={(event) => setNodeLabel(event.target.value)} required placeholder="For example, staff onboarding" />
            )}
            <TextField label="Department or stage" value={nodeStage} onChange={(event) => setNodeStage(event.target.value)} placeholder="For example, Service Delivery" />
            <TextField label="Notes" value={nodeNotes} onChange={(event) => setNodeNotes(event.target.value)} multiline minRows={2} />
            {nodeKind === "PLAYBOOK" && selectedPlaybook?.objective && (
              <Alert severity="info">{selectedPlaybook.objective}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNode} disabled={saving}>{saving ? "Saving…" : "Save item"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingLink ? "Edit connection" : "Connect map items"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField select label="From" value={linkFromNodeId} onChange={(event) => setLinkFromNodeId(event.target.value)} disabled={Boolean(editingLink)}>
              {nodes.map((node) => <MenuItem key={node.id} value={node.id}>{getNodeTitle(node)}</MenuItem>)}
            </TextField>
            <TextField select label="To" value={linkToNodeId} onChange={(event) => setLinkToNodeId(event.target.value)} disabled={Boolean(editingLink)}>
              {nodes.map((node) => <MenuItem key={node.id} value={node.id}>{getNodeTitle(node)}</MenuItem>)}
            </TextField>
            <TextField label="Connection label" value={linkLabel} onChange={(event) => setLinkLabel(event.target.value)} placeholder="For example, audit accepted" />
            <TextField label="Outcome key (optional)" value={linkOutcomeKey} onChange={(event) => setLinkOutcomeKey(event.target.value)} />
            <FormControlLabel control={<Switch checked={linkIsDefault} onChange={(event) => setLinkIsDefault(event.target.checked)} />} label="Default path" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLink} disabled={saving || nodes.length < 2}>{saving ? "Saving…" : "Save connection"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageBusinessStructure;
